import { Injectable, OnDestroy } from '@angular/core';
import { ApolloError } from '@apollo/client/core';
import { TdDialogService } from '@covalent/core/dialogs';
import { Store } from '@ngrx/store';
import { Apollo } from 'apollo-angular';
import { DocumentNode } from 'graphql';
import { Observable, race, Subject } from 'rxjs';
import { filter, map, takeUntil, tap, timeout } from 'rxjs/operators';
import { CommandResultStatus } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { CommandResult, CommandResultError } from 'src/util/graphql/command-result';
import { MutationError, MutationRequest, MutationSuccess } from 'src/util/graphql/mutation-actions';
import { stringify } from 'src/util/json';
import { lowerCaseFirstLetter } from 'src/util/string';
import { uuid } from 'src/util/uuid';
import {
  OperationAlertHandler,
  OperationAlertService,
  OperationAlertType,
} from '../operation-alert.service';

// request timeout for mutations
const MUTATION_REQUEST_TIMEOUT = 1000 * 30;

interface Command {
  mutation: DocumentNode;
  variables: any;
}
interface Operation {
  id: string;
  alert: OperationAlertHandler;
}
interface CacheItem {
  [operationId: string]: { alert: OperationAlertHandler };
}

export interface MutationSpec {
  mutationFactory: () => DocumentNode;
}

export interface GraphQLMutationExecutionOptions {
  alert:
    | {
        description: string;
        verbosity?: OperationAlertType;
      }
    | OperationAlertHandler;
}

@Injectable({
  providedIn: 'root',
})
export class GraphqlMutationExecutorService implements OnDestroy {
  private _cache: Map<string, CacheItem> = new Map();
  private _onDestroy$ = new Subject();

  constructor(
    private readonly _store: Store<AppState>,
    private readonly _alerts: OperationAlertService,
    private readonly _apollo: Apollo,
    private readonly _td: TdDialogService,
  ) {}

  private createOperation(
    name: string,
    command: Command,
    createAlertFn: (operationId: string) => OperationAlertHandler,
  ): Operation {
    const operationId = name + '-' + uuid();
    const json = stringify(command);
    const operations = this._cache.get(json) ?? {};

    operations[operationId] = { alert: createAlertFn(operationId) };
    this._cache.set(json, operations);
    return { id: operationId, alert: operations[operationId].alert };
  }

  private completeOperation(command: Command, completeFn: (operation: Operation) => void) {
    const json = stringify(command);
    const operations = this._cache.get(json);
    if (operations != null) {
      for (const operationId in operations) {
        if (operations[operationId] != null) {
          const alert = operations[operationId].alert;
          completeFn({ id: operationId, alert });
        }
      }
      this._cache.delete(json);
    }
  }

  public execute<TName extends string, TOperation, TVariables, TResult>({
    name,
    variables,
    mutation,
    options,
  }: {
    name: TName;
    variables: TVariables;
    mutation: DocumentNode;
    options: GraphQLMutationExecutionOptions;
  }): Observable<CommandResult<TResult>> {
    const command: Command = { mutation, variables };
    const operation = this.createOperation(name, command, (operationId) =>
      options.alert instanceof OperationAlertHandler
        ? options.alert
        : this._alerts.createAlert({
            operationId,
            description: options.alert.description,
            verbosity: options.alert.verbosity != null ? options.alert.verbosity : 'executing',
          }),
    );

    const operationCancelled$ = operation.alert.onAction$.pipe(
      filter((a) => a.type === 'cancelled'),
    );

    const requestAction = new MutationRequest(name, operation.id, variables);
    this._store.dispatch(requestAction);
    operation.alert.markAsExecuting();

    const destroy$ = race(operationCancelled$, this._onDestroy$);
    return this._apollo
      .mutate<TOperation, TVariables>({
        mutation,
        variables,
        context: { operationId: operation.id },
      })
      .pipe(
        timeout(MUTATION_REQUEST_TIMEOUT),
        map((result) => {
          const dataKey = lowerCaseFirstLetter(name);
          const typedResult = (result.data as any)[dataKey] as CommandResult<TResult>;
          if (typedResult.status !== CommandResultStatus.Succeeded) {
            throw new CommandResultError(name, typedResult);
          }
          return typedResult;
        }),
        tap(
          (success) => {
            this.completeOperation(command, ({ id, alert }) => {
              const successAction = new MutationSuccess(name, id, success);
              this._store.dispatch(successAction);
              alert.markAsSuccess();
            });
          },
          (error: CommandResultError | ApolloError) => {
            this.completeOperation(command, ({ id, alert }) => {
              const errorAction = new MutationError(
                name,
                id,
                error instanceof CommandResultError ? error.result : undefined,
              );
              this._store.dispatch(errorAction);

              if (
                error instanceof ApolloError ||
                error.result == null ||
                error.result.status === CommandResultStatus.Failed
              ) {
                let errorMessage = '';
                if (error.message != null) {
                  error.message = error.message.replace('GraphQL error:', '');
                  errorMessage =
                    error.message.length > 200
                      ? error.message.substring(0, 200) + '...'
                      : error.message;
                }

                const isUserError =
                  error instanceof CommandResultError && error.userErrors.length > 0;
                if (isUserError) {
                  this._td.openAlert({
                    title: 'Error: ' + alert.options.description,
                    message: error.message,
                    hasBackdrop: true,
                    width: error.message.length > 200 ? '1000px' : '400px',
                  });
                }
                alert.markAsError({
                  message: errorMessage,
                  details: error,
                });
              } else {
                alert.markAsWarning({
                  message: error.message,
                  details: error,
                });
              }
            });
          },
        ),
        takeUntil(destroy$),
      );
  }

  public ngOnDestroy(): void {
    this._onDestroy$.next(null);
    this._onDestroy$.complete();
  }
}
